<script setup>
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDocs } from "../../composables/useDocs.js";
import { useInfiniteScroll } from "../../composables/useInfiniteScroll.js";
import { permissionsForRole } from "../../composables/usePermissions.js";
import { truncateMiddle } from "../../composables/useTextTruncate.js";
import Spinner from "../ui/Spinner.vue";
import PanelSortMenu from "../ui/PanelSortMenu.vue";
import DownloadOnlyModal from "../ui/DownloadOnlyModal.vue";
import DocumentCardMenu from "../ui/DocumentCardMenu.vue";
import { useDocumentSync } from "../../composables/useDocumentSync.js";

const props = defineProps({
  isActive: { type: Boolean, default: false }, // panel is mounted app-wide (v-show); only fetch once it's actually opened
});
const emit = defineEmits(["close"]);

const route = useRoute();
const router = useRouter();

// Shares the exact same fetched list as the Docs page — same singleton-only
// document source, so both stay in sync and a refresh (e.g. after an upload)
// updates whichever is currently visible.
const { docs: allDocs, isLoading, isLoadingMore, hasMore, load, loadMore, resort } = useDocs();

const documentSync = useDocumentSync();

// Download-only files (isAnnotatable: false) get their own section at the
// bottom, starred or not — same split as ProjectsPanel.vue.
const annotatableDocs = computed(() => allDocs.value.filter((d) => d.isAnnotatable !== false));
const starredDocs = computed(() => annotatableDocs.value.filter((d) => d.isFavorite));
const otherDocs = computed(() => annotatableDocs.value.filter((d) => !d.isFavorite));
const downloadOnlyDocs = computed(() => allDocs.value.filter((d) => d.isAnnotatable === false));

// All Documents always shows (it carries the empty state's context); the
// other two only when they have something in them.
const docSections = computed(() => [
  { key: "starred", label: "Starred", docs: starredDocs.value },
  { key: "all", label: "All Documents", docs: otherDocs.value, alwaysShow: true },
  { key: "download-only", label: "Download only files", docs: downloadOnlyDocs.value },
].filter((section) => section.alwaysShow || section.docs.length));

// Non-annotatable docs (can't be previewed) open a permission-gated download
// popup instead of navigating — same behavior as Home.vue's document table.
const downloadOnlyDoc = ref(null);

function openDoc(doc) {
  if (doc.isAnnotatable === false) {
    downloadOnlyDoc.value = doc;
    return;
  }
  router.push({ name: "v2-document", params: { projectId: doc.projectId, docId: doc.id } });
}
function isActiveDoc(doc) {
  return route.name === "v2-document" && route.params.docId === doc.id;
}

// Each standalone doc is backed by its own singleton project, so the
// permission set gating its menu actions and downloads comes from that doc's
// own role/profile fields (populated by ProjectService.getSingletonProjects),
// not a shared "current project" like ProjectsPanel.vue has.
function projectFor(doc) {
  const role = doc.projectUserRole ?? "OWNER";
  return {
    id: doc.projectId,
    userRole: role,
    isSingleton: true,
    ownerProfile: doc.ownerProfile,
    topCollaborators: doc.topCollaborators,
    permissions: permissionsForRole(role),
  };
}

watch(() => props.isActive, (active) => { if (active) load(); }, { immediate: true });

const SORT_OPTIONS = [
  { key: "NAME",          label: "Name" },
  { key: "DATE_ADDED",    label: "Date added" },
  { key: "LAST_MODIFIED", label: "Last modified" },
];
const sortBy = ref("DATE_ADDED");
function onSelectSort(key) {
  sortBy.value = key;
  resort(key, "DESC");
}

const sentinelEl = ref(null);
useInfiniteScroll(sentinelEl, () => loadMore());
</script>

<template>
    <div class="bg-white  dark:bg-inherit w-[256px] h-full">
      <div class="flex mt-3 flex-col h-full w-[244px] bg-gray-100 dark:bg-slate-900 rounded-t-2xl overflow-hidden">
        <!-- Header — same gray as the list body below it, one continuous surface -->
        <div class="flex  border-b-2 border-white dark:border-slate-600 items-center justify-between px-4 py-3 shrink-0">
          <div class="flex items-baseline gap-1.5 flex-1 min-w-0">
            <span class="text-[15px] font-semibold text-slate-800 dark:text-slate-100 truncate">Docs</span>
            <span class="text-[12px] text-slate-400 dark:text-slate-500 truncate">(No project assigned)</span>
          </div>
          <button
            class="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer transition-colors shrink-0 ml-2"
            @click="emit('close')"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" />
            </svg>
          </button>
        </div>
    
        <!-- Loading -->
        <div v-if="isLoading" class="flex justify-center py-10"><Spinner size="22px" /></div>
    
        <!-- Doc list — Starred + All Documents share one continuous gray body,
             same structure as ProjectsPanel.vue's per-project doc list. -->
        <div v-else class="flex-1 min-h-0 overflow-y-auto py-3 px-2">
    
          <template v-for="(section, index) in docSections" :key="section.key">
            <div v-if="index > 0" class="mx-2 my-2 h-px bg-slate-200 dark:bg-slate-700" />
            <p :data-testid="`docs-panel-${section.key}-label`" class="text-[11px] text-slate-400 dark:text-slate-500 px-2 pt-1 pb-1 m-0">{{ section.label }} ({{ section.docs.length }})</p>
            <div
              v-for="doc in section.docs"
              :key="doc.id"
              :title="doc.name"
              :data-testid="`docs-panel-${section.key}-doc`"
              :class="isActiveDoc(doc)
                ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400'
                : 'hover:bg-slate-200 dark:hover:bg-slate-700/60'"
              class="flex items-center gap-2.5 px-2 py-2.5 rounded-xl cursor-pointer group transition-colors"
              @click="openDoc(doc)"
            >
              <svg class="shrink-0" :class="isActiveDoc(doc) ? 'text-blue-500 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'" width="20" height="22" viewBox="0 0 24 26" fill="none">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
              <span :class="isActiveDoc(doc) ? 'text-blue-600 dark:text-blue-400' : 'text-slate-800 dark:text-slate-100'" :title="doc.name ?? 'Untitled'" class="flex-1 text-[14px] font-medium truncate min-w-0">{{ truncateMiddle(doc.name ?? "Untitled") }}</span>
              <DocumentCardMenu
                data-testid="docs-panel-doc-menu"
                :document="doc"
                :project="projectFor(doc)"
                allow-favorite-toggle
                trigger-class="flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-300 dark:hover:bg-slate-600 border-0 bg-transparent cursor-pointer shrink-0 transition-colors"
                @renamed="documentSync.renamed(doc.id, $event.name)"
                @starred="documentSync.favoriteChanged(doc.id, true)"
                @unstarred="documentSync.favoriteChanged(doc.id, false)"
                @deleted="documentSync.removed(doc.id, projectFor(doc))"
                @rejected="documentSync.removed(doc.id, projectFor(doc))"
              />
            </div>
          </template>

          <p v-if="!allDocs.length" class="text-xs text-slate-400 dark:text-slate-500 text-center py-6 px-4 m-0">No documents found</p>
    
          <!-- Infinite scroll sentinel -->
          <div ref="sentinelEl" class="h-px" />
          <div v-if="isLoadingMore" class="flex justify-center py-3"><Spinner size="16px" /></div>
        </div>
      </div>

      <DownloadOnlyModal
        v-if="downloadOnlyDoc"
        :document="downloadOnlyDoc"
        :project="projectFor(downloadOnlyDoc)"
        @close="downloadOnlyDoc = null"
      />
    </div>
</template>
